import { useState } from 'react'

import { NAV, type Route } from '../../app/nav'
import { api } from '../../lib/api'
import { useSession } from '../../lib/session'
import { cn } from '../../lib/utils'
import { Icon } from '../icon'

/**
 * The sidebar — all seventeen entries, in the design's order.
 *
 * 240px, `panel`, one `line` border on the right, full height. The active item is the design's:
 * `accent-soft` fill, `accent` text, and a 2px inset bar on the left, which is what makes the
 * state readable at a glance in a list this long.
 *
 * Nothing decorates an entry except a count, and only where a real number exists. The design's dot
 * slot is unused: a marker against twelve of seventeen entries is noise, and each of those screens
 * already says its endpoint is missing in a band across the top of the page — which is where an
 * operator is looking when it matters.
 *
 * Below `lg` it becomes an overlay at 252px. No shadow — the scrim behind it is the separation.
 */
export function Sidebar({
  route,
  counts,
  onNavigate,
  open,
  onClose,
}: {
  route: Route
  counts: Partial<Record<Route, number>>
  onNavigate: (route: Route) => void
  open: boolean
  onClose: () => void
}) {
  const { user, refresh, isPlatformStaff } = useSession()
  const [filter, setFilter] = useState('')

  // Platform-plane entries are hidden from every customer, because a sidebar full of
  // controls that answer "this is not yours" is a sidebar that teaches people to ignore it.
  // Presentation only — both screens refuse on their own and every endpoint behind them is
  // guarded by @PlatformRoute() on the server.
  const entries = NAV.filter(
    (entry) =>
      (isPlatformStaff || !entry.platformOnly) &&
      (!filter || entry.label.toLowerCase().includes(filter.toLowerCase())),
  )

  return (
    <aside
      className={cn(
        'bg-panel border-line z-50 flex shrink-0 flex-col border-r transition-transform duration-150',
        'fixed inset-y-0 left-0 w-[252px] lg:static lg:w-60 lg:translate-x-0',
        open ? '' : '-translate-x-full',
      )}
    >
      {/* 52px, matching the top bar, so the hairline under both runs unbroken across the seam. */}
      <div className="border-line flex h-[52px] shrink-0 items-center gap-[9px] border-b px-3">
        <div className="bg-accent h-[22px] w-[22px] shrink-0 rounded-[6px]" aria-hidden />
        <div className="flex min-w-0 items-baseline gap-[7px]">
          <span className="text-text text-[13.5px] font-semibold tracking-[-0.01em] whitespace-nowrap">
            Mail Room
          </span>
          <span className="text-muted border-line rounded-full border px-[7px] py-px text-[10.5px] whitespace-nowrap">
            Development
          </span>
        </div>
      </div>

      <div className="shrink-0 px-2.5 pt-2.5 pb-2">
        <div className="bg-panel2 border-line focus-within:border-accent flex items-center gap-[7px] rounded-[7px] border px-2 py-1.5 transition-colors">
          <Icon name="search" size={12} className="text-faint" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter navigation"
            aria-label="Filter navigation"
            className="text-text min-w-0 flex-1 border-0 bg-transparent text-[12px] outline-none"
          />
        </div>
      </div>

      <nav className="flex min-h-0 flex-1 flex-col gap-px overflow-x-hidden overflow-y-auto px-2.5 pt-1 pb-2.5">
        {entries.map((entry) => {
          const active = entry.id === route
          const count = counts[entry.id]

          return (
            <button
              key={entry.id}
              onClick={() => {
                onNavigate(entry.id)
                onClose()
              }}
              aria-current={active ? 'page' : undefined}
              title={entry.label}
              className={cn(
                'flex w-full min-w-0 items-center gap-[9px] overflow-hidden rounded-[7px] px-2.5 py-[7px] text-left text-[14.5px] font-medium transition-colors',
                active
                  ? 'bg-accent-soft text-accent font-semibold'
                  : 'text-muted hover:bg-hover hover:text-text',
              )}
            >
              <Icon name={entry.icon} size={16} strokeWidth={1.7} className={active ? 'opacity-100' : 'opacity-80'} />
              <span className="flex-1 truncate">{entry.label}</span>

              {count !== undefined && (
                <span className="text-faint shrink-0 text-[10px]">{count.toLocaleString()}</span>
              )}
            </button>
          )
        })}

        {entries.length === 0 && (
          <p className="text-faint px-2.5 py-3 text-[12px]">Nothing matches “{filter}”.</p>
        )}
      </nav>

      <div className="border-line flex h-[52px] shrink-0 items-center gap-2 border-t px-3">
        <div className="bg-sel text-muted flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full text-[10px] font-medium">
          {(user?.name ?? '?').slice(0, 1).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-text truncate text-[12.5px] leading-tight">{user?.name}</p>
          <p className="text-faint truncate text-[11px] leading-tight">{user?.email}</p>
        </div>
        <button
          onClick={async () => {
            await api.post('/auth/logout')
            await refresh()
          }}
          className="text-faint hover:text-text shrink-0 text-[11.5px] transition-colors"
        >
          Sign out
        </button>
      </div>
    </aside>
  )
}
